import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import HeadsetMicOutlinedIcon       from "@mui/icons-material/HeadsetMicOutlined";
import AccountTreeOutlinedIcon      from "@mui/icons-material/AccountTreeOutlined";
import AutoAwesomeOutlinedIcon      from "@mui/icons-material/AutoAwesome";
import WorkOutlinedIcon             from "@mui/icons-material/WorkOutlined";
import QueryStatsOutlinedIcon       from "@mui/icons-material/QueryStats";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import { Sparkles as SparklesIcon } from "lucide-react";
import HexagonOutlinedIcon          from "@mui/icons-material/HexagonOutlined";
import PublicIcon                   from "@mui/icons-material/Public";
import WifiIcon                     from "@mui/icons-material/Wifi";
import LinkIcon                     from "@mui/icons-material/Link";
import CloudOutlinedIcon            from "@mui/icons-material/CloudOutlined";

import mascot1 from "../../../../design-system/assets/mascot/teli-thumbsup.png";
import mascot2 from "../../../../design-system/assets/mascot/teli-wave.png";
import mascot3 from "../../../../design-system/assets/mascot/teli-tablet.png";
import mascot4 from "../../../../design-system/assets/mascot/teli-arms.png";
import orbitImg from "../../../../design-system/assets/hero-orbit.png";
import bgSkyImg from "../../../../design-system/assets/bg-sky2.png";

const CHIPS = [
  { label: "HD Calls",          icon: HeadsetMicOutlinedIcon,         color: "#1FB6D8", bg: "#DEF3F8" },
  { label: "Smart Routing",     icon: AccountTreeOutlinedIcon,        color: "#5B5BE6", bg: "#E8E8FB" },
  { label: "AI Assistant",      icon: AutoAwesomeOutlinedIcon,        color: "#7A3FE4", bg: "#EEE8FC" },
  { label: "CRM",               icon: WorkOutlinedIcon,               color: "#3B7DF0", bg: "#E5EEFD" },
  { label: "Analytics",         icon: QueryStatsOutlinedIcon,         color: "#22B07D", bg: "#DEF3EC" },
  { label: "Follow up Tickets", icon: ConfirmationNumberOutlinedIcon, color: "#F59E42", bg: "#FDEEDE" },
];

const TRUST_LOGOS = [
  { name: "TechNova",        Icon: HexagonOutlinedIcon },
  { name: "Global Solutions",Icon: PublicIcon          },
  { name: "NextWave",        Icon: WifiIcon            },
  { name: "Bright Link",     Icon: LinkIcon            },
  { name: "Cloudify",        Icon: CloudOutlinedIcon   },
];

const MASCOTS = [
  { src: mascot1, delay: "0s",  alt: "Teli giving thumbs up"  },
  { src: mascot2, delay: "3s",  alt: "Teli waving hello"      },
  { src: mascot3, delay: "6s",  alt: "Teli with tablet"       },
  { src: mascot4, delay: "9s",  alt: "Teli with open arms"    },
];

export default function Hero() {
  const { t } = useTranslation("home");
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        pt: "64px", // header height

        // background: sky plate + gradient overlay
        background: `linear-gradient(180deg,#F5F6FC 0%,#F3F4FC 24%,rgba(243,244,252,0) 60%),url(${bgSkyImg}) center bottom / cover`,

        // Keyframes injected here — available to all descendants
        // 12 s cycle · 3 s stagger · 1 s fade-in, 2 s hold, 1 s fade-out
        // → perfect 1 s crossfade overlap between consecutive mascots
        "@keyframes heroCycle": {
          "0%":      { opacity: 0, transform: "translateX(-50%) translateY(14px)" },
          "8.33%":   { opacity: 1, transform: "translateX(-50%) translateY(0px)"  },
          "25%":     { opacity: 1, transform: "translateX(-50%) translateY(0px)"  },
          "33.33%":  { opacity: 0, transform: "translateX(-50%) translateY(-10px)" },
          "100%":    { opacity: 0, transform: "translateX(-50%) translateY(0px)"  },
        },
        "@keyframes haloPulse": {
          "0%,100%": { opacity: 0.5, transform: "translate(-50%,-50%) scale(1)" },
          "50%":     { opacity: 0.72, transform: "translate(-50%,-50%) scale(1.05)" },
        },
        "@keyframes ringSpin":  { from: { transform: "translate(-50%,-50%) rotate(0deg)"   }, to: { transform: "translate(-50%,-50%) rotate(360deg)"  } },
        "@keyframes ringSpinR": { from: { transform: "translate(-50%,-50%) rotate(0deg)"   }, to: { transform: "translate(-50%,-50%) rotate(-360deg)" } },
        "@keyframes particle": {
          "0%":   { transform: "translateY(10px)",   opacity: 0   },
          "20%":  { opacity: 0.6 },
          "80%":  { opacity: 0.6 },
          "100%": { transform: "translateY(-110px)", opacity: 0   },
        },
        "@keyframes auroraDrift": {
          "0%,100%": { transform: "translate(0,0) scale(1)"      },
          "50%":     { transform: "translate(-2.5%,2%) scale(1.05)" },
        },
      }}
    >
      {/* Aurora color blobs */}
      <Box sx={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: "radial-gradient(54% 62% at 18% 40%,rgba(245,246,252,.82),rgba(245,246,252,.20) 48%,transparent 74%)",
      }} />
      <Box sx={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: "radial-gradient(38% 40% at 14% 22%,rgba(31,192,218,.08),transparent 60%),radial-gradient(42% 46% at 88% 72%,rgba(122,63,228,.07),transparent 62%)",
        animation: "auroraDrift 26s ease-in-out infinite",
      }} />

      {/* Content grid */}
      <Box sx={{
        position: "relative",
        zIndex: 2,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "1.04fr .96fr" },
        gap: { xs: "48px", lg: "24px" },
        alignItems: "center",
        maxWidth: 1320,
        mx: "auto",
        px: { xs: 3, md: "56px" },
        pt: { xs: "32px", md: "26px" },
        pb: { xs: "48px", md: "60px" },
      }}>

        {/* ── Left column ── */}
        <Box>
          {/* Eyebrow */}
          <Box sx={{
            display: "inline-flex", alignItems: "center", gap: "8px",
            px: "15px", py: "7px", borderRadius: "999px",
            background: "rgba(255,255,255,.72)", backdropFilter: "blur(10px)",
            border: "1px solid #fff", boxShadow: "0 2px 8px rgba(23,26,56,.06)",
            fontSize: "12.5px", fontWeight: 600, color: "#6D28D9",
            fontFamily: "var(--font-ui)",
          }}>
            <SparklesIcon size={14} color="#1FC0DA" />
            {t("heroEyebrow")}
          </Box>

          {/* Headline */}
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: "2.5rem", sm: "3rem", md: "3.125rem", lg: "3.25rem" },
              lineHeight: 1.04,
              fontWeight: 800,
              letterSpacing: "-0.025em",
              color: "#12142E",
              mt: "20px",
              mb: 0,
              fontFamily: "var(--font-display)",
            }}
          >
            {t("heroHeadPart1")}
            <br />
            <Box
              component="span"
              sx={{
                background: "linear-gradient(90deg,#1FC0DA,#4B78EC 55%,#7A3FE4)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {t("heroHeadGradient")}
            </Box>
            {" "}{t("heroHeadPart2")}
          </Typography>

          {/* Sub-copy */}
          <Typography sx={{
            fontSize: { xs: "1rem", md: "17px" },
            lineHeight: 1.6,
            color: "#4A5075",
            maxWidth: "44ch",
            mt: "18px",
            fontFamily: "var(--font-ui)",
          }}>
            {t("heroSubCopy")}
          </Typography>

          {/* CTA row */}
          <Box sx={{ display: "flex", gap: "14px", mt: "26px", flexWrap: "wrap" }}>
            <Box
              component="button"
              onClick={() => navigate("/signup")}
              sx={{
                height: 52, px: "26px", borderRadius: "999px",
                border: "none", cursor: "pointer",
                background: "linear-gradient(135deg,#1FC0DA,#4B78EC 50%,#7A3FE4)",
                fontFamily: "var(--font-ui)", fontWeight: 600, fontSize: "16px",
                color: "#fff", boxShadow: "var(--shadow-cta)",
                transition: "transform 0.18s ease, box-shadow 0.2s ease, filter 0.18s ease",
                "&:hover": { transform: "translateY(-2px)", filter: "brightness(1.04)", boxShadow: "0 12px 28px rgba(90,70,220,.38)" },
                "&:active": { transform: "translateY(0)" },
              }}
            >
              {t("heroStartFree")}
            </Box>
            <Box
              component="button"
              onClick={() => navigate("/offers")}
              sx={{
                height: 52, px: "26px", borderRadius: "999px",
                border: "1px solid #E7E9F4", background: "#fff",
                cursor: "pointer",
                fontFamily: "var(--font-ui)", fontWeight: 600, fontSize: "16px",
                color: "#1B1F3B", boxShadow: "0 1px 2px rgba(23,26,56,.06)",
                transition: "transform 0.18s ease, box-shadow 0.2s ease",
                "&:hover": { transform: "translateY(-1px)", boxShadow: "var(--shadow-card)" },
                "&:active": { transform: "translateY(0)" },
              }}
            >
              {t("heroBookDemo")}
            </Box>
          </Box>

          {/* Feature chips */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "20px", mt: "34px" }}>
            {CHIPS.map(({ label, icon: Icon, color, bg }, i) => (
              <Box key={label} sx={{
                display: "flex", flexDirection: "column", alignItems: "center",
                gap: "8px", width: "66px", textAlign: "center",
              }}>
                <Box sx={{
                  width: 46, height: 46, borderRadius: "50%",
                  background: bg,
                  display: "grid", placeItems: "center",
                  boxShadow: "0 4px 12px rgba(46,55,120,.08)",
                }}>
                  <Icon sx={{ fontSize: 21, color }} />
                </Box>
                <Typography sx={{
                  fontSize: "11px", color: "#4A5075", fontWeight: 500,
                  lineHeight: 1.2, fontFamily: "var(--font-ui)",
                }}>
                  {t(`chip${i}`, { defaultValue: label })}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Trust bar */}
          <Typography sx={{
            mt: "34px", fontSize: "12px", color: "#9AA0BD",
            fontWeight: 600, fontFamily: "var(--font-ui)",
          }}>
            {t("heroTrustedBy")}
          </Typography>
          <Box sx={{
            display: "flex", gap: "22px", mt: "12px",
            flexWrap: "wrap", alignItems: "center",
          }}>
            {TRUST_LOGOS.map(({ name, Icon }) => (
              <Box key={name} sx={{
                display: "flex", alignItems: "center", gap: "7px",
              }}>
                <Box sx={{
                  width: 22, height: 22, borderRadius: "50%",
                  background: "rgba(174,179,204,.18)",
                  display: "grid", placeItems: "center", flexShrink: 0,
                }}>
                  <Icon sx={{ fontSize: 13, color: "#AEB3CC" }} />
                </Box>
                <Typography sx={{
                  fontSize: "13.5px", fontWeight: 700,
                  color: "#AEB3CC", fontFamily: "var(--font-ui)",
                }}>
                  {name}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── Right column: orbit art ── */}
        <Box sx={{
          display: { xs: "none", lg: "flex" },
          justifyContent: "center",
        }}>
          <Box sx={{
            position: "relative",
            width: "min(560px, 100%)",
            mx: "auto",
            aspectRatio: "1122 / 1402",
          }}>
            {/* Violet halo glow */}
            <Box sx={{
              position: "absolute",
              left: "50%", top: "47%",
              width: "118%", height: "118%",
              transform: "translate(-50%,-50%)",
              borderRadius: "50%",
              background: "radial-gradient(closest-side,rgba(122,63,228,.20),rgba(59,125,240,.09) 55%,transparent 72%)",
              filter: "blur(8px)",
              animation: "haloPulse 7s ease-in-out infinite",
              pointerEvents: "none",
            }} />

            {/* Outer dashed orbit ring */}
            <Box sx={{
              position: "absolute",
              left: "50%", top: "47%",
              width: "98%", height: "98%",
              transform: "translate(-50%,-50%)",
              borderRadius: "50%",
              border: "1.5px dashed rgba(122,63,228,.15)",
              animation: "ringSpin 64s linear infinite",
              pointerEvents: "none",
            }} />

            {/* Inner dashed ring */}
            <Box sx={{
              position: "absolute",
              left: "50%", top: "47%",
              width: "76%", height: "76%",
              transform: "translate(-50%,-50%)",
              borderRadius: "50%",
              border: "1px dashed rgba(31,192,218,.16)",
              animation: "ringSpinR 52s linear infinite",
              pointerEvents: "none",
            }} />

            {/* Particles */}
            {[
              { l: "24%", t: "66%", c: "rgba(122,63,228,.45)", s: 6, d: "0s",   dur: "9s" },
              { l: "74%", t: "70%", c: "rgba(31,192,218,.5)",  s: 5, d: "0.8s", dur: "11s" },
              { l: "60%", t: "57%", c: "rgba(59,125,240,.5)",  s: 4, d: "0.4s", dur: "8s" },
              { l: "38%", t: "74%", c: "rgba(122,63,228,.4)",  s: 5, d: "1.4s", dur: "12s" },
            ].map(({ l, t, c, s, d, dur }, i) => (
              <Box key={i} sx={{
                position: "absolute", left: l, top: t,
                width: s, height: s, borderRadius: "50%",
                background: c, filter: "blur(.5px)",
                animation: `particle ${dur} ease-in-out ${d} infinite`,
                pointerEvents: "none",
              }} />
            ))}

            {/* Orbit image (ring + planet bubbles pre-rendered) */}
            <Box
              component="img"
              src={orbitImg}
              alt="Teleredo capabilities"
              sx={{
                position: "absolute", inset: 0,
                width: "100%", height: "100%",
                objectFit: "contain",
              }}
            />

            {/* Shadow under mascot */}
            <Box sx={{
              position: "absolute",
              left: "50%", bottom: "18.5%",
              transform: "translateX(-50%)",
              width: "38%", height: "4.5%",
              background: "radial-gradient(closest-side,rgba(46,55,120,.30),transparent 72%)",
              filter: "blur(7px)",
              pointerEvents: "none",
            }} />

            {/* Mascots (cycling crossfade — 1 s fade-in/out, 2 s hold, 3 s stagger) */}
            {MASCOTS.map(({ src, delay, alt }, i) => (
              <Box
                key={delay}
                component="img"
                src={src}
                alt={t(`mascotAlt${i}`, { defaultValue: alt })}
                sx={{
                  position: "absolute",
                  left: "50%", bottom: "20%",
                  height: "62%", width: "auto",
                  objectFit: "contain",
                  filter: "drop-shadow(0 20px 30px rgba(46,55,120,.18))",
                  opacity: 0,
                  animation: "heroCycle 12s ease-in-out infinite both",
                  animationDelay: delay,
                  willChange: "opacity, transform",
                }}
              />
            ))}
          </Box>

        </Box>
      </Box>
    </Box>
  );
}
